import {useQuery} from 'react-query'
import {parseISO} from 'date-fns'
import {Pagination} from 'common/types'
import {api, useMutationQuery} from 'base'
import {ProjectType} from 'modules/project'
import { showSuccessNotification } from 'common/helpers'

interface UseGetProjectsParams {
  type?: ProjectType
  enabled?: boolean
  managerUuid?: string
  filter?: any
  sort?: string
  search?: string
  pageNumber?:number;
}

export const useGetProjects = ({
  type = 'current',
  enabled,
  filter,
  search,
}: UseGetProjectsParams = {}) =>
  useQuery<any[]>(
    ['projects', type, {filter, search}],
    async () => {
      const {data} = await api.get('/project/list', {
        params: {
          filter: {
            ...filter,
            search,
            status: type === 'closed' ? 'closed' : 'opened',
          },
        },
      })
      return data.data
    },
    {enabled, keepPreviousData: true}
  )

export const useGet1PageProjects = ({
  type = 'current',
  enabled,
  filter,
  sort,
  search,
  pageNumber,
}: UseGetProjectsParams = {}) => {
  return useQuery<Pagination<any[]>>(
    ['projects', type, {sort, filter, search, pageNumber}],
    async () => {
      const {data} = await api.get('/project/list', {
        params: {
          sort,
          filter: {
            ...filter,
            search,
            status: type === 'closed' ? 'closed' : 'opened',
          },
          page:pageNumber,
          paginate: !!pageNumber,
          perPage: 10,
        },
      })
      return data
    },
    {enabled, keepPreviousData: true}
  )
}

export const useGetBrokerProjects = () =>
  useQuery<any[]>('projects', async () => {
    const {data} = await api.get('/project/list')
    const sortedData: any[] = data.data.slice()
    sortedData.sort(
      (a, b) => +parseISO(a.maturityDate) - +parseISO(b.maturityDate)
    )
    return sortedData
  })

export const useGetUserProjects = () =>
  useQuery<any[]>('projects', async () => {
    const {data} = await api.get('/project/list')
    return data.data
  })

export const useGetTotalXirr = () =>
  useQuery<any>('totalXirr', async () => {
    const {data} = await api.get('/project/xirr')
    return data.data.xirr
  })

export const useGetAnalyticBalance = () => {
  return useQuery<any>(
    ['analyticBalance'],
    async () => {
      const {data} = await api.get('/analytic/projectBalance')
      return data
    }
  )
}

export const useGetAnalyticIRR = () => {
  return useQuery<any>(
    ['analyticIRR'],
    async () => {
      const {data} = await api.get('/analytic/projectTotalIRR')
      return data
    }
  )
}

export const useGetAnalyticCashflow = () => {
  return useQuery<any>(
    ['analyticCashflow'],
    async () => {
      const {data} = await api.get('/analytic/cashflowForecast')
      return data
    }
  )
}

export const useImportProject = () => {
  return useMutationQuery<any>(
    ['importProject'],
    async (file) => {
      const data = await api.post('/import/project', { file }, {
        headers: {
          'Content-Type': 'multipart/form-data',  
        }
      });
      return data.data;
    },
    {
      onSuccess(response) {
        showSuccessNotification(response.message);
      }
    }
  )
}

export const useExportClients = () => {
  return useMutationQuery<any>(
    ['exportClients'],
    async (reportType) => {
      const res = await api.get(`/import/download/${reportType}`, {
        responseType: 'blob'
      });
      return { blob: res.data, reportType };
    },
    {
      onSuccess({blob, reportType }) {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = reportType;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    }
  )
}
